import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AutoCompleteComponent, SelectFieldComponent } from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import { ROWS_PER_PAGE } from "../App";
import DateUtils from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import { AppToolbar } from "../components/AppToolbar";
import Certification from "../models/Certification";
import CertificationService from "../services/CertificationService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { SimpleAutoComplete } from "../components/SimpleAutoComplete";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import { TextField, Typography } from "@mui/material";
import { SelectComponent } from "../components/SelectComponent";
import { CertificationStatus } from "../utils/enums/Certification/CertificationStatus";
import { CertificationLevel } from "../utils/enums/Certification/CertificationLevel";
import Badge from "../models/Badge";
import BadgeService from "../services/BadgeService";
import RoleUtils from "../utils/RoleUtils";
import { AppDialog } from "../components/AppDialog";
import CertificationStatusDialog from "../components/Certification/CertificationStatusDialog";
import CertificationViewDialog from "../components/Certification/CertificationViewDialog";
import OpsAvatarComponent from "../components/OpsAvatarComponent";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import UserService from "../services/UserService";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import useWindowSize from "../hooks/useWindowSize";
import { makeStyles } from "@material-ui/core";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import useTableHeight from "../hooks/useTableHeight";

export interface ActionsMenuEntry {
  title: string;
  action: () => void;
}

export default function CertificationList() {
  const { t } = useTranslation();
  const classes = useStyles();

  const [selectedCertification, setSelectedCertification] = useState<Certification | undefined>(undefined);
  const [selectedBadge, setSelectedBadge] = useState<AutoCompleteUIDTO>(EMPTY_AUTOCOMPLETE);
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [level, setLevel] = useState<string>("");
  const [badgesNames, setBadgesNames] = useState<AutoCompleteUIDTO[]>([]);
  const [activeBadgesNames, setActiveBadgesNames] = useState<AutoCompleteUIDTO[]>([]);
  const [isLevelOptionAvailable, setIsLevelOptionAvailable] = useState<boolean>(false);
  const [statusToSet, setStatusToSet] = useState<CertificationStatus>();
  const [userExternalId, setUserExternalId] = useState<string>();
  const [viewCertification, setViewCertification] = useState<Certification | undefined>();
  const [actualBadge, setActualBadge] = useState<Badge>();
  const [isOpen, setIsOpen] = useState(false);
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  useEffect(() => {
    getAllBadgesNames();
    UserService.findByCoreUserId(AuthService.getUser()?.externalId!)
      .then((response) => setUserExternalId(response.getContentOrThrowError().externalId))
      .catch(() => NotificationService.error("Error getting user"));
  }, []);

  const getAllBadgesNames = () => {
    BadgeService.findAllBadgesNames(false)
      .then((response) => setBadgesNames(response.getContentOrThrowError()))
      .catch(() => NotificationService.error("Error getting badges names"));
    BadgeService.findAllBadgesNames(true)
      .then((response) => setActiveBadgesNames(response.getContentOrThrowError()))
      .catch(() => NotificationService.error("Error getting active badges names"));
  };

  const handleOnChangeBadge = async (selectedBadge: AutoCompleteUIDTO) => {
    if (selectedBadge === null) {
      setIsLevelOptionAvailable(false);
      return;
    }
    const badge = (await BadgeService.getByExternalId(selectedBadge.externalId)).getContentOrThrowError();
    setIsLevelOptionAvailable(badge.level);
    setActualBadge(badge);
    console.log(badge.automaticApproval);
  };

  const handleOnDeleteCertification = async () => {
    if (!selectedCertification) return;

    try {
      await CertificationService.delete(selectedCertification);
      setSelectedCertification(undefined);
      NotificationService.info("Certification deleted successfully");
    } catch (error: any) {
      NotificationService.error(error.response.data.message);
    }
  };

  const handleOpenViewDialog = (instance: Certification) => {
    setViewCertification(instance);
  };

  const getAdditionalActions = (instance: Certification): ActionsMenuEntry[] => {
    let options = Array<ActionsMenuEntry>();

    if (RoleUtils.isAdmin() && CertificationStatus[instance.status as keyof typeof CertificationStatus] === CertificationStatus.PENDING) {
      options.push({
        title: "Approve",
        action: () => {
          setSelectedCertification(instance);
          setStatusToSet(CertificationStatus.APPROVED);
        },
      });
    }

    if (
      RoleUtils.isAdmin() &&
      (CertificationStatus[instance.status as keyof typeof CertificationStatus] === CertificationStatus.APPROVED ||
        CertificationStatus[instance.status as keyof typeof CertificationStatus] === CertificationStatus.PENDING)
    ) {
      options.push({
        title: "Deny",
        action: () => {
          setSelectedCertification(instance);
          setStatusToSet(CertificationStatus.DENIED);
        },
      });
    }

    if (instance.ownerExternalId === userExternalId) {
      options.push({
        title: "Delete",
        action: () => {
          setSelectedCertification(instance);
        },
      });
    }
    return options;
  };

  const getDialogEdit = (register: any, errors: any, control: any, instance?: Certification): JSX.Element => {
    return (
      <>
        <AutoCompleteComponent
          control={control}
          register={register}
          errors={errors}
          propertyName={"badge.externalId"}
          label="Badge Name"
          required
          options={activeBadgesNames}
          onChange={(data) => handleOnChangeBadge(data)}
        />
        <div className="mx-auto d-flex justify-content-center" style={{ marginTop: "2em", marginBottom: "2em" }}>
          <OpsAvatarComponent
            avatarUrl={actualBadge?.iconUrl ? actualBadge?.iconUrl : ""}
            forceGenericAvatar={true}
            genericAvatar={<NoPhotographyIcon sx={{ width: "4em", height: "4em" }} />}
            sx={{ width: "6.5em", height: "6.5em", marginBottom: "1em" }}
            customStyle={{ maxWidth: "50%", display: "flex", justifyContent: "center", margin: "auto" }}
          />
        </div>
        {actualBadge?.description && <Typography style={{ marginLeft: "1em", marginBottom: "2em" }}>{actualBadge?.description}</Typography>}
        {isLevelOptionAvailable && (
          <SelectFieldComponent
            control={control}
            register={register}
            errors={errors}
            propertyName={"level"}
            label={"Level"}
            required
            options={Object.values(CertificationLevel)
              .filter((value) => value !== CertificationLevel.ALL)
              .map((u) => ({ label: u, value: u }))}
          />
        )}
      </>
    );
  };

  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        {RoleUtils.isAdmin() ? (
          <TextField
            label="Team member"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearch(e.target.value)}
            size="small"
          />
        ) : (
          <SimpleAutoComplete
            label="Badge name"
            items={[{ label: "All", externalId: "All" }, ...badgesNames]}
            onChange={(newValue) => {
              setSelectedBadge(newValue);
            }}
            value={selectedBadge}
          />
        )}
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {RoleUtils.isAdmin() && (
          <TextField
            label="Team member"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearch(e.target.value)}
            size="small"
          />
        )}
        <SimpleAutoComplete
          label="Badge name"
          items={[{ label: "All", externalId: "All" }, ...badgesNames]}
          onChange={(newValue) => {
            setSelectedBadge(newValue);
          }}
          value={selectedBadge}
        />
        <SelectComponent
          label={"Level"}
          size="small"
          value={level ? level : ""}
          options={[...Object.values(CertificationLevel), "N/A"]}
          setValue={setLevel}
        />
        <SelectComponent
          label={"Status"}
          size="small"
          value={status ? status : ""}
          options={Object.values(CertificationStatus)}
          setValue={setStatus}
        />
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = (
    <>
      {RoleUtils.isAdmin() && (
        <SimpleAutoComplete
          label="Badge name"
          items={[{ label: "All", externalId: "All" }, ...badgesNames]}
          onChange={(newValue) => {
            setSelectedBadge(newValue);
          }}
          value={selectedBadge}
        />
      )}
      <SelectComponent
        label={"Level"}
        size="small"
        value={level ? level : ""}
        options={[...Object.values(CertificationLevel), "N/A"]}
        setValue={setLevel}
      />
      <SelectComponent
        label={"Status"}
        size="small"
        value={status ? status : ""}
        options={Object.values(CertificationStatus)}
        setValue={setStatus}
      />
    </>
  );

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={<AppToolbar title="Certifications">{renderFilters()}</AppToolbar>}
      bottomSection={
        <>
          <TableBasedCrud<Certification>
            maxHeight={calculateMaxHeight(true)}
            stickyHeader
            elementName={"Certification"}
            rowsPerPage={ROWS_PER_PAGE}
            denseTable
            delayFetch={search}
            onDataFetch={async (pageNumber, count) =>
              (
                await CertificationService.findFiltered(pageNumber, count, selectedBadge?.externalId, search, status, level)
              ).getContentOrThrowError()
            }
            onCreate={(instance) =>
              CertificationService.createCertification(instance)
                .then(() => NotificationService.info("Certification created successfully"))
                .catch((err) => NotificationService.error(err.response.data.message))
            }
            onCancelDialog={() => {
              setIsLevelOptionAvailable(false);
              setActualBadge(undefined);
            }}
            onViewMode={(instance) => handleOpenViewDialog(instance)}
            buildAdditionalActions={getAdditionalActions}
            renderDialogContent={(register, errors, control, instance) => getDialogEdit(register, errors, control, instance || undefined)}
            tableColumns={
              RoleUtils.isAdmin()
                ? [
                    {
                      header: t("Team member"),
                      content: (instance) => <>{instance.ownerFullName}</>,
                    },
                    {
                      header: t("Badge"),
                      content: (instance) => <>{instance.badgeName}</>,
                    },
                    {
                      header: t("Applied on"),
                      content: (instance) => <>{DateUtils.formatAsDDMMMYYY(instance.createdAt)}</>,
                    },
                    {
                      header: t("Level"),
                      content: (instance) => (
                        <>{instance.level ? CertificationLevel[instance.level as keyof typeof CertificationLevel] : "N/A"}</>
                      ),
                    },
                    {
                      header: t("Status"),
                      content: (instance) => <>{CertificationStatus[instance.status as keyof typeof CertificationStatus]}</>,
                    },
                  ]
                : [
                    {
                      header: t("Badge"),
                      content: (instance) => <>{instance.badgeName}</>,
                    },
                    {
                      header: t("Applied on"),
                      content: (instance) => <>{DateUtils.formatAsDDMMMYYY(instance.createdAt)}</>,
                    },
                    {
                      header: t("Level"),
                      content: (instance) => (
                        <>{instance.level ? CertificationLevel[instance.level as keyof typeof CertificationLevel] : "N/A"}</>
                      ),
                    },
                    {
                      header: t("Status"),
                      content: (instance) => <>{CertificationStatus[instance.status as keyof typeof CertificationStatus]}</>,
                    },
                  ]
            }
          />
          {/* Dialog to delete sites */}
          <AppDialog
            open={!!selectedCertification && statusToSet === undefined}
            onClose={() => setSelectedCertification(undefined)}
            title={`Delete certification of ${selectedCertification?.ownerFullName}`}
            content={
              <Typography>
                {`The certification of the next badge is going to be eliminated: ${selectedCertification?.badgeName}. 
            Do you want to confirm this action?`}
              </Typography>
            }
            buttons={[
              {
                label: "Confirm delete",
                onClick: () => {
                  handleOnDeleteCertification();
                },
              },
            ]}
          />
          {selectedCertification && statusToSet && (
            <CertificationStatusDialog
              certification={selectedCertification}
              onClose={() => {
                setSelectedCertification(undefined);
                setStatusToSet(undefined);
              }}
              status={statusToSet!}
              onChangeStatus={async () => {
                (await CertificationService.changeStatus(selectedCertification.externalId, statusToSet!))
                  .onSuccess(() => NotificationService.info("Certification status updated successfully"))
                  .onError((error) => NotificationService.error(error.getContent()));
                setSelectedCertification(undefined);
                setStatusToSet(undefined);
              }}
            />
          )}
          {viewCertification && (
            <CertificationViewDialog
              viewCertification={viewCertification}
              onClose={() => {
                setViewCertification(undefined);
                setActualBadge(undefined);
              }}
              isLoading={false}
            />
          )}
          {/* Dialog to manage filters  */}
          <FilterDialog title={"Certifications"} setIsOpen={setIsOpen} isOpen={isOpen}>
            {renderDialogFilters}
          </FilterDialog>
        </>
      }
    />
  );
}
const useStyles = makeStyles((theme) => ({
  desktopFilters: {
    padding: "5px",
    display: "flex",
    flexDirection: "row",
    "&> div:first-child": {
      minWidth: "150px",
      marginLeft: "0",
    },
    "&> div:nth-child(2)": {
      minWidth: "150px",
    },
  },
  mobileFilters: {
    "&> div:first-child": {
      minWidth: "150px",
      width: "100%",
      marginLeft: "0",
    },
    padding: "5px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  [theme.breakpoints.down("sm")]: {
    desktopFilters: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridRowGap: "10px",
      gridColumnGap: "10px",
      "& div": {
        margin: "0 !important",
      },
    },
  },
}),
{ index: 1 }
);
